import { Checkbox, TableCell, TableRow, TableRowProps } from '@material-ui/core';
import React, { useCallback } from 'react';
import { ColDef, RowData, RowId } from '.';
import DataTableCellElement from './DataTableCellElement';
import { useStyles } from './DataTableRowElement.styles';
import { useDisabledRowTooltip } from './hooks/useDisabledRowTooltip';
import DisabledRowTooltip from './components/DisabledRowTooltip';
import DisabledRowOverlay from './components/DisabledRowOverlay';
import { checkRowSelectable, getRowDisabledState, getCustomRowClassName, RowCustomizationProps } from './tableUtils';

export interface DataTableRowElementProps extends TableRowProps, RowCustomizationProps {
  rowData: RowData;
  columns: ColDef[];
  checkboxSelection: boolean;
  rowHeight: number;
  index: number;
  disableSelectionOnClick?: boolean;
  isItemSelected?: boolean;
  rowClickCallback?: (rowData: RowData) => void;
  rowSelectionCallback?: (rowId: RowId) => void;
  cellClickCallback?: (column: ColDef, value: any, rowData: RowData) => void;
  isSelected?: (rowId: RowId) => boolean;
  subElement?: boolean;
  setOpen?:(param:boolean)=>void;
  open?: boolean;
}

const DataTableRowElement = (props: DataTableRowElementProps) => {
  const classes = useStyles();
  const {
    rowData,
    columns,
    rowHeight,
    checkboxSelection,
    index,
    disableSelectionOnClick,
    isItemSelected,
    isSelected,
    rowClickCallback,
    rowSelectionCallback,
    cellClickCallback,
    subElement,
    setOpen,
    open,
    isRowSelectable,
    isRowDisabled,
    getRowClassName,
    ...rest
  } = props;

  // Check if current row is selectable using utility
  const isCurrentRowSelectable = checkRowSelectable(rowData, isRowSelectable);
  
  // Get disabled state using utility
  const { disabled: isCurrentRowDisabled, reason: disabledReason } = getRowDisabledState(rowData, isRowDisabled);
  
  // Use custom hook for tooltip management
  const { tooltipPosition, handleMouseMove, handleMouseLeave } = useDisabledRowTooltip(isCurrentRowDisabled);
  
  // Determine if row should show "not clickable" state
  const isNotClickable = isCurrentRowDisabled || (checkboxSelection && !disableSelectionOnClick);

  const handleRowSelection = useCallback(
    () => {
      if (!isCurrentRowSelectable || isCurrentRowDisabled) return;
      rowSelectionCallback && rowSelectionCallback(rowData.id);
    },
    [rowData, rowSelectionCallback, isCurrentRowSelectable, isCurrentRowDisabled],
  );

  const handleRowClick = useCallback(
    (event: React.MouseEvent<HTMLTableRowElement, MouseEvent>) => {
      if (isCurrentRowDisabled) {
        event.stopPropagation();
        event.preventDefault();
        return;
      }
      if (event && event.target && event.target instanceof HTMLInputElement) {
        return;
      }
      if (checkboxSelection) {
        if (disableSelectionOnClick) {
          rowClickCallback && rowClickCallback(rowData);
          return;
        }
        handleRowSelection();
      } else {
        rowClickCallback && rowClickCallback(rowData);
      }
    },
    [checkboxSelection, disableSelectionOnClick, handleRowSelection, rowClickCallback, rowData, isCurrentRowSelectable, isCurrentRowDisabled],
  );

  // Get custom row class name using utility
  const customRowClassName = getCustomRowClassName(rowData, getRowClassName);

  // Combine base class with custom class
  const getRowClass = () => {
    const baseClass = index % 2 === 0 ? classes.root : classes.evenRoot;
    return customRowClassName ? `${baseClass} ${customRowClassName}` : baseClass;
  };

  return (
    <>
      <TableRow
        {...rest}
        className={getRowClass()}
        style={{
          height: rowHeight,
          minHeight: rowHeight,
          maxHeight: rowHeight,
          position: 'relative',
          cursor: isNotClickable ? 'default' : 'pointer',
        }}
        selected={isItemSelected}
        onClick={handleRowClick}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
      >
        {checkboxSelection && (
          <TableCell>
            <Checkbox
              checked={isItemSelected}
              size="small"
              color="primary"
              onChange={handleRowSelection}
              disabled={subElement || !isCurrentRowSelectable || isCurrentRowDisabled}
            />
          </TableCell>
        )}
    
        {columns.map((col: ColDef, index: number) => (
          <DataTableCellElement
            key={`${col.field}${index}`}
            column={col}
            rowHeight={rowHeight}
            value={rowData[col.field]}
            rowData={rowData}
            cellClickCallback={cellClickCallback}
            setOpen={setOpen}
            open={open}
            subElement={subElement && true} 
          />
        ))}
        
        <DisabledRowOverlay isDisabled={isCurrentRowDisabled} />
      </TableRow>
      
      <DisabledRowTooltip 
        position={tooltipPosition} 
        message={disabledReason}
      />
    </>
  );
}

export default DataTableRowElement;